import { useTradeSettingsStore } from '@/stores/tradeSettingsStore'
import { booleanFormatter, loadFromStorage } from '@/utils'
import { computed, onMounted, watch, type Ref } from 'vue'

export const useTrailingSlSettings = (root: Ref<string | null>) => {
  const tradeSettingsStore = useTradeSettingsStore()

  const trailingSl = computed({
    get: () =>
      root.value ? (tradeSettingsStore.getSetting(root.value, 'trailingSl') ?? false) : false,
    set: (value: boolean) => {
      if (root.value) {
        tradeSettingsStore.setSetting(root.value, 'trailingSl', value)
      }
    },
  })

  watch(trailingSl, (v) => {
    localStorage.setItem('trailing-sl', v ? '1' : '0')
  })

  const trailingActive = computed({
    get: () =>
      root.value ? (tradeSettingsStore.getSetting(root.value, 'trailingActive') ?? false) : false,
    set: (value: boolean) => {
      if (root.value) {
        tradeSettingsStore.setSetting(root.value, 'trailingActive', value)
      }
    },
  })

  const trailingActivatedAt = computed({
    get: () =>
      (root.value
        ? (tradeSettingsStore.getSetting(root.value, 'trailingActivatedAt') ?? null)
        : null) as number | null,
    set: (value: number | null) => {
      if (root.value) {
        tradeSettingsStore.setSetting(root.value, 'trailingActivatedAt', value)
      }
    },
  })

  const trailingSide = computed({
    get: () =>
      root.value
        ? (tradeSettingsStore.getSetting(root.value, 'trailingSide') ?? undefined)
        : undefined,
    set: (value: string | undefined) => {
      if (root.value) {
        tradeSettingsStore.setSetting(root.value, 'trailingSide', value)
      }
    },
  })

  const trailingExit = computed({
    get: () =>
      (root.value ? (tradeSettingsStore.getSetting(root.value, 'trailingExit') ?? null) : null) as
        | number
        | null,
    set: (value: number | null) => {
      if (root.value) {
        tradeSettingsStore.setSetting(root.value, 'trailingExit', value)
      }
    },
  })

  const trailingStep = computed({
    get: () =>
      (root.value ? (tradeSettingsStore.getSetting(root.value, 'trailingStep') ?? null) : null) as
        | number
        | null,
    set: (value: number | null) => {
      if (root.value) {
        tradeSettingsStore.setSetting(root.value, 'trailingStep', value)
      }
    },
  })

  const trailingNext = computed({
    get: () =>
      (root.value ? (tradeSettingsStore.getSetting(root.value, 'trailingNext') ?? null) : null) as
        | number
        | null,
    set: (value: number | null) => {
      if (root.value) {
        tradeSettingsStore.setSetting(root.value, 'trailingNext', value)
      }
    },
  })

  const cleanTrailingSl = () => {
    trailingActive.value = false
    trailingActivatedAt.value = null
    trailingSide.value = undefined
    trailingExit.value = null
    trailingStep.value = null
    trailingNext.value = null
  }

  onMounted(() => {
    trailingSl.value = loadFromStorage<boolean>('trailing-sl-cex', booleanFormatter, true)
  })

  const initTrailingStopLoss = (
    isLong: boolean,
    anchorPrice: number,
    avgSpread: number | null | undefined,
    spread: number | null,
    backSpread: number | null,
  ) => {
    const avg = avgSpread ?? 0
    const longSpread = Number(spread) - avg
    const shortSpread = Number(backSpread) + avg
    const finalSpread =
      Math.abs(isLong ? longSpread - (avgSpread ?? 0) : shortSpread + (avgSpread ?? 0)) * 0.5
    trailingActive.value = false
    trailingActivatedAt.value = +new Date()
    trailingStep.value = (anchorPrice * 1) / 100
    const initExitStep = (anchorPrice * Math.max(finalSpread, 1)) / 100
    if (isLong) {
      trailingSide.value = 'long'
      trailingExit.value = anchorPrice - initExitStep
      trailingNext.value = anchorPrice + trailingStep.value
    } else {
      trailingSide.value = 'short'
      trailingExit.value = anchorPrice + initExitStep
      trailingNext.value = anchorPrice - trailingStep.value
    }

    console.debug(`anchorPrice: ${anchorPrice}`)
    console.debug(`trailingSide: ${trailingSide.value}`)
    console.debug(`trailingExit: ${trailingExit.value}`)
    console.debug(`trailingNext: ${trailingNext.value}`)
  }

  return {
    cleanTrailingSl,
    initTrailingStopLoss,
    trailingSl,
    trailingActive,
    trailingActivatedAt,
    trailingSide,
    trailingExit,
    trailingStep,
    trailingNext,
  }
}
